<template>
  <div class="certificate-work-order">
    <div class="issue-container">
      <div class="issue-wrap">
        <page-header
          :title="pageTitle"
          :sub-title="pageSubTitle"
        />
        <progress-wrap-four
          :step="currentStep + 1"
          :progressTexts="progressTexts"
        />

        <div class="step-content">
        <!-- Step 1: 병원 선택 -->
        <div v-if="currentStep === 0" class="step-panel">
          <Step1
            ref="step1"
            @select-client="handleClientSelection"
          />
        </div>

        <!-- Step 2: 환자 정보 입력 -->
        <div v-if="currentStep === 1" class="step-panel">
          <Step2
            ref="step2"
          />
        </div>

        <!-- Step 3: 상세 정보 입력 -->
        <div v-if="currentStep === 2" class="step-panel">
          <Step3
            ref="step3"
            :initial-data="workOrderData"
          />
        </div>

        <!-- Step 4: 보철 인증서 전달 정보 확인 -->
        <div v-if="currentStep === 3" class="step-panel">
          <Step4
            ref="step4"
            :client-data="selectedClient"
            :customer-data="certificateInfo"
            :prosthetic-data="workOrderData"
            :prevStep="prevStep"
            :complete="complete"
          />
        </div>
      </div>

      <div class="step-footer" v-if="currentStep < 3">
        <div class="button-group">
          <button
            @click="prevStep"
            :disabled="currentStep === 0"
            class="btn-secondary"
          >
            <i class="fa-solid fa-arrow-left"></i>
            {{ $t('workorder_btn_prev') }}
          </button>

          <button
            v-if="currentStep < steps.length - 1"
            @click="nextStep"
            :disabled="!canProceed"
            class="btn-primary"
          >
            {{ $t('workorder_btn_next') }}
            <i class="fa-solid fa-arrow-right"></i>
          </button>
          <button
            v-else
            @click="complete"
            class="btn-primary"
          >
            <i class="fa-solid fa-paper-plane"></i>
            {{ $t('workorder_btn_submit') }}
          </button>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Step1 from './components/Step1.vue';
import Step2 from './components/Step2.vue';
import Step3 from './components/Step3.vue';
import Step4 from './components/Step4.vue';
import PageHeader from './components/PageHeader.vue';
import ProgressWrapFour from './components/ProgressWrapFour.vue';
import { WorkOrderService } from './services/WorkOrderService';
import { ValidationUtils } from './utils/ValidationUtils';
import { STEPS } from './constants/WorkOrderConstants';
import { businessNumberFormatter } from '@/utils/utils';

export default {
  name: 'CertificateWorkOrder',
  components: {
    Step1,
    Step2,
    Step3,
    Step4,
    PageHeader,
    ProgressWrapFour
  },
  data() {
    return {
      currentStep: 0,
      selectedClient: null,
      certificateInfo: {},
      workOrderData: {},
      workOrderService: null
    }
  },
  created() {
    this.workOrderService = new WorkOrderService(this.$store);
  },
  computed: {
    pageTitle() {
      return this.$t('workorder_title');
    },
    pageSubTitle() {
      return `${this.$t('workorder_desc_1')}<br/>${this.$t('workorder_desc_2')}<br/>${this.$t('workorder_desc_3')}`;
    },
    progressTexts() {
      return [
        this.$t('workorder_step1_title'),
        this.$t('workorder_step2_title'),
        this.$t('workorder_step3_title'),
        this.$t('workorder_step4_title')
      ];
    },
    
    steps() {
      return STEPS;
    },
    
    canProceed() {
      switch(this.currentStep) {
        case 0:
          return this.selectedClient !== null;
        case 1:
          // Step2는 내부 검증을 통해 확인
          return true;
        case 2:
          // Step3 검증 로직 추가
          return true;
        default:
          return true;
      }
    }
  },
  methods: {
    formatBusinessNumber(value) {
      return businessNumberFormatter(value);
    },
    handleClientSelection(client) {
      this.selectedClient = client;
      console.log('선택된 거래처:', client);
    },
    async nextStep() {
      const validation = await this.validateCurrentStep();
      
      if (!validation.isValid) {
        this.$message.warning(validation.message);
        return;
      }

      await this.saveCurrentStepData();

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },

    async validateCurrentStep() {
      switch(this.currentStep) {
        case 0:
          return ValidationUtils.validateStep1(this.selectedClient);
        case 1:
          return await ValidationUtils.validateStep2(this.$refs.step2);
        case 2:
          return await ValidationUtils.validateStep3(this.$refs.step3);
        default:
          return { isValid: true };
      }
    },

    async saveCurrentStepData() {
      console.log('[saveCurrentStepData] currentStep:', this.currentStep);
      switch(this.currentStep) {
        case 1:
          this.certificateInfo = this.$refs.step2?.getFormData();
          console.log('[saveCurrentStepData] certificateInfo 저장:', this.certificateInfo);
          break;
        case 2:
          this.workOrderData = this.$refs.step3?.getFormData();
          console.log('[saveCurrentStepData] workOrderData 저장:', this.workOrderData);
          break;
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    goToStep(index) {
      // 완료된 스텝으로만 이동 가능
      if (index <= this.currentStep) {
        this.currentStep = index;
      }
    },
    async complete() {
      console.log('complete 메소드 호출');
      
      // 최종 데이터 검증
      const finalValidation = ValidationUtils.validateFinalData(
        this.selectedClient, 
        this.certificateInfo, 
        this.workOrderData
      );
      
      if (!finalValidation.isValid) {
        console.error('최종 검증 실패:', finalValidation.errors);
        this.$message.error(finalValidation.errors.join(', '));
        return;
      }

      // 전달할 데이터 준비
      const workOrderData = this.workOrderService.prepareWorkOrderData(
        this.selectedClient,
        this.certificateInfo,
        this.workOrderData
      );

      if (!workOrderData) {
        this.$message.error(this.$t('workorder_err_required'));
        return;
      }

      // API 호출
      await this.workOrderService.createWorkOrder(
        workOrderData,
        (response) => {
          console.log('API 성공 응답:', response);
          this.$message.success(this.$t('workorder_success_msg'));
          this.$router.push('/certificate-work-order-list');
        },
        (error) => {
          console.error('전달 실패:', error);
          this.$message.error(this.$t('workorder_err_submit'));
        }
      );
    },

  }
}
</script>

<style scoped lang="scss">
$Gray-10: #F1F2F3;
$Gray-20: #E8EBED;
$Gray-30: #DDE0E4;
$Gray-40: #C7CCD1;
$Gray-50: #ACB2B9;
$Gray-60: #8D939A;
$Gray-70: #6e747c;
$Gray-80: #4B5158;
$Gray-90: #2F3337;
$Gray-100 : #17191C;
$Yellow: #ffe414;
$Blue : #278AEC;
$SkyBlue : #E1F0FF;

.certificate-work-order {
  max-width: 100%;
  display: flex;
  align-items: start;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  background-color: $Gray-10;
  min-height: 100vh;

  .issue-container {
    width: 100%;
    max-width: 1000px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    flex-direction: column;

    .issue-wrap {
      width: 100%;
      max-width: 1000px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 24px;
      padding: 24px 24px 48px 24px;
      flex-direction: column;
    }
  }
}


.step-content {
  width: 100%;
  max-width: 1000px;

  .step-panel {
    animation: fadeIn 0.3s ease-in-out;
    width: 100%;
  }
}

.step-footer {
  width: 100%;
  max-width: 1000px;
  padding: 24px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .button-group {
    display: flex;
    gap: 12px;
  }

  button {
    padding: 12px 32px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    justify-content: center;

    i {
      font-size: 16px;
    }

    &.btn-secondary {
      background: white;
      color: $Gray-70;
      border: 2px solid $Gray-30;

      &:hover:not(:disabled) {
        background: $Gray-10;
        border-color: $Gray-50;
        color: $Gray-90;
      }
    }

    &.btn-primary {
      background: $Yellow;
      color: $Gray-90;
      border: none;

      &:hover:not(:disabled) {
        background: #ffd700;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(255, 228, 20, 0.3);
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      transform: none !important;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 반응형 디자인
@media (max-width: 768px) {
  .certificate-work-order {
    .issue-container {
      .issue-wrap {
        padding: 16px 16px 32px 16px;
        gap: 16px;
      }
    }
  }

  .step-footer {
    padding: 16px 0;

    .button-group {
      flex-direction: column;
      width: 100%;
      gap: 8px;
    }

    button {
      width: 100%;
      padding: 14px 24px;
      font-size: 16px;
    }
  }
}
</style>

<style lang="scss" scoped>
@import './components/css/issue-certificate.scss';
@import './components/css/implant-abutment.scss';
</style>